import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import JobForm from '../components/JobForm';
import Pagination from '../components/Pagination'; // Import the new component

// Job Status Badge Component
function StatusBadge({ status }) {
    return <span className={`status-badge ${status}`}>{status}</span>;
}

// Job Details Component
function JobDetails({ job }) {
    if (!job.meta) return null;

    const handleRestart = async () => {
        if (window.confirm(`Are you sure you want to restart job ${job.job_id}?`)) {
            try {
                await axios.post(`/api/job/restart/${job.job_id}`);
            } catch (error) {
                alert("Failed to restart job. See console for details.");
                console.error("Error restarting job:", error);
            }
        }
    };

    const renderResult = () => {
        if (job.status === 'finished') {
            return job.result && job.result.length > 0 ? (
                <div>
                    <h4>Generated Videos:</h4>
                    <ul>
                        {job.result.map((url, index) => (
                            <li key={index}><a href={url} target="_blank" rel="noopener noreferrer">{url}</a></li>
                        ))}
                    </ul>
                </div>
            ) : <p>Finished but no result URL was returned.</p>;
        }
        if (job.status === 'failed') {
            return (
                <div>
                    <h4>Error:</h4>
                    <pre>{job.exc_info || 'An unknown error occurred.'}</pre>
                    <button onClick={handleRestart} className="secondary">Restart Job</button>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="job-details">
            <p><strong>Last status detail:</strong> {job.meta.status_detail || 'N/A'}</p>
            {job.server_name && <p><strong>Processed by:</strong> {job.server_name}</p>}
            {job.meta.source_url && <p><strong>Source URL:</strong> <a href={job.meta.source_url} target="_blank" rel="noopener noreferrer">{job.meta.source_url}</a></p>}
            {job.config_overrides && Object.keys(job.config_overrides).length > 0 && (
                <div>
                    <h4>Config Overrides Applied:</h4>
                    <pre>{JSON.stringify(job.config_overrides, null, 2)}</pre>
                </div>
            )}
            {renderResult()}
        </div>
    );
}

// Helper to format duration in seconds to a human-readable string
const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined || seconds < 0) return 'N/A';
    if (seconds < 1) return '< 1s';
    const s = Math.floor(seconds % 60);
    const m = Math.floor((seconds / 60) % 60);
    const h = Math.floor(seconds / 3600);
    let result = '';
    if (h > 0) result += `${h}h `;
    if (m > 0) result += `${m}m `;
    if (s > 0 || (h === 0 && m === 0)) result += `${s}s`;
    return result.trim();
};

// New component for the live timer
function LiveDuration({ startTime }) {
    const [duration, setDuration] = useState(Date.now() - new Date(startTime));

    useEffect(() => {
        const interval = setInterval(() => {
            setDuration(Date.now() - new Date(startTime));
        }, 1000);

        return () => clearInterval(interval);
    }, [startTime]);

    return <span>{formatDuration(duration / 1000)}</span>;
}

// Component for sort indicators
function SortIndicator({ direction }) {
    if (!direction) return null;
    return <span style={{ marginLeft: '5px' }}>{direction === 'asc' ? '▲' : '▼'}</span>;
}

// Main Jobs Page Component
export default function JobsPage() {
    const [jobs, setJobs] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [serverFilter, setServerFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
    const [availableServers, setAvailableServers] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null); // Changed from ID to the full job object
    const [showJobForm, setShowJobForm] = useState(false);
    
    // --- New state for pagination ---
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchJobs = useCallback(async () => {
        try {
            const status = statusFilter === 'all' ? '' : `/${statusFilter}`;
            
            const params = new URLSearchParams();
            if (serverFilter) params.append('server_name', serverFilter);
            if (dateFilter) params.append('date', dateFilter);
            params.append('page', currentPage);
            params.append('per_page', 10); // Or make this configurable
            params.append('sort_by', sortConfig.key);
            params.append('sort_order', sortConfig.direction);
            
            const endpoint = `/api/jobs/status${status}?${params.toString()}`;
            
            const response = await axios.get(endpoint);
            
            setJobs(response.data.jobs);
            setTotalPages(response.data.total_pages);
        } catch (error) {
            console.error("Error fetching jobs:", error);
        }
    }, [statusFilter, serverFilter, dateFilter, currentPage, sortConfig]);

    const fetchServers = async () => {
        try {
            const response = await axios.get('/api/servers/');
            setAvailableServers(response.data);
        } catch (err) {
            console.error("Error fetching servers:", err);
        }
    };

    const fetchJobDetails = async (jobId) => {
        try {
            const response = await axios.get(`/api/job/${jobId}/status`);
            setSelectedJob(response.data); // Store the full job object separately
        } catch (error) {
            console.error(`Error fetching details for job ${jobId}:`, error);
        }
    };

    useEffect(() => {
        fetchServers(); // Fetch servers once on mount
    }, []);
    
    useEffect(() => {
        const interval = setInterval(fetchJobs, 3000);
        fetchJobs();
        return () => clearInterval(interval);
    }, [fetchJobs]);

    const toggleDetails = (jobId) => {
        if (selectedJob && selectedJob.job_id === jobId) {
            setSelectedJob(null);
        } else {
            fetchJobDetails(jobId);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, serverFilter, dateFilter]);

    return (
        <>
            <div style={{marginBottom: '2rem'}}>
                <button onClick={() => setShowJobForm(!showJobForm)}>
                    {showJobForm ? 'Hide Form' : 'Create New Job'}
                </button>
                {showJobForm && <JobForm onJobAdded={() => { fetchJobs(); setShowJobForm(false); }} />}
            </div>

            <h2>Job Queue</h2>
            <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem'}}>
                <div className="form-group">
                    <label>Status</label>
                    <select onChange={(e) => setStatusFilter(e.target.value)} value={statusFilter}>
                        <option value="all">All</option>
                        <option value="queued">Queued</option>
                        <option value="started">In Progress</option>
                        <option value="finished">Finished</option>
                        <option value="failed">Failed</option>
                    </select>
                </div>
                <div className="form-group">
                    <label>Server</label>
                    <select onChange={(e) => setServerFilter(e.target.value)} value={serverFilter}>
                        <option value="">All Servers</option>
                        {availableServers.map(server => (
                            <option key={server.name} value={server.name}>{server.name}</option>
                        ))}
                    </select>
                </div>
                <div className="form-group">
                    <label>Date</label>
                    <input type="date" onChange={(e) => setDateFilter(e.target.value)} value={dateFilter} />
                </div>
            </div>
            
            <table className="table-container">
                <thead>
                    <tr>
                        <th className="sortable" onClick={() => handleSort('job_id')}>
                            Job ID <SortIndicator direction={sortConfig.key === 'job_id' ? sortConfig.direction : null} />
                        </th>
                        <th className="sortable" onClick={() => handleSort('server_name')}>
                            Server <SortIndicator direction={sortConfig.key === 'server_name' ? sortConfig.direction : null} />
                        </th>
                        <th className="sortable" onClick={() => handleSort('status')}>
                            Status <SortIndicator direction={sortConfig.key === 'status' ? sortConfig.direction : null} />
                        </th>
                        <th className="sortable" onClick={() => handleSort('created_at')}>
                            Created At <SortIndicator direction={sortConfig.key === 'created_at' ? sortConfig.direction : null} />
                        </th>
                        <th className="sortable" onClick={() => handleSort('ended_at')}>
                            Ended At <SortIndicator direction={sortConfig.key === 'ended_at' ? sortConfig.direction : null} />
                        </th>
                        <th className="sortable" onClick={() => handleSort('duration')}>
                            Duration <SortIndicator direction={sortConfig.key === 'duration' ? sortConfig.direction : null} />
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {jobs.map(job => (
                        <React.Fragment key={job.job_id}>
                            <tr style={{background: selectedJob?.job_id === job.job_id ? 'var(--surface-color)' : 'transparent' }}>
                                <td>{job.job_id.split('-')[0]}...</td>
                                <td>{job.server_name || 'N/A'}</td>
                                <td><StatusBadge status={job.status} /></td>
                                <td>{new Date(job.created_at).toLocaleString()}</td>
                                <td>{job.ended_at ? new Date(job.ended_at).toLocaleString() : 'N/A'}</td>
                                <td>
                                    {job.status === 'started' && job.started_at ? (
                                        <LiveDuration startTime={job.started_at} />
                                    ) : (
                                        formatDuration(job.duration)
                                    )}
                                </td>
                                <td>
                                    <button onClick={() => toggleDetails(job.job_id)} className="secondary">
                                        {selectedJob?.job_id === job.job_id ? 'Hide' : 'Details'}
                                    </button>
                                </td>
                            </tr>
                            {selectedJob?.job_id === job.job_id && (
                                <tr>
                                    <td colSpan="7"><JobDetails job={selectedJob} /></td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            <Pagination 
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />
        </>
    );
} 